// src/components/KnowledgeBaseExceptionsTester.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
    ExceptionStatus,
    ExceptionCategory,
    KnowledgeScope,
    KBException,
} from '../../../../store/sofia/exceptions/exceptionsApi';
import {
    tryToFetchKnowledgeBaseExceptions,
    tryToFetchKnowledgeBaseExceptionById,
    clearKnowledgeBaseExceptionsError,
    tryToUpdateKnowledgeBaseExceptionStatus,
    tryToResolveKnowledgeBaseException,
    tryToConfirmResolveKnowledgeBaseException,
} from '../../../../store/sofia/exceptions/knowledgeBaseExceptionsSlice';
import { useSearchParams } from 'react-router-dom';
import { UsageQuotaCard } from '../../../../components/shared/UsageQuotaCard';
import {
    AiQuotaResponse,
    KbQuotaResponse,
    UsageService,
} from '../../../../store/sofia/usage/usageApi';

function cx(...classes: Array<string | false | undefined | null>) {
    return classes.filter(Boolean).join(' ');
}

function formatStatus(s: ExceptionStatus) {
    if (s === 'pending') return 'Pending';
    if (s === 'in_progress') return 'In Progress';
    return 'Resolved';
}

function formatCategory(c: ExceptionCategory) {
    return c;
}

function formatWhen(iso?: string) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function statusBadgeClasses(status: ExceptionStatus) {
    switch (status) {
        case 'pending':
            return 'bg-red-50 text-red-700 ring-red-200';
        case 'in_progress':
            return 'bg-blue-50 text-blue-700 ring-blue-200';
        case 'resolved':
            return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    }
}

function categoryBadgeClasses(category: ExceptionCategory) {
    switch (category) {
        case 'Hotel Term':
            return 'bg-zinc-50 text-zinc-700 ring-zinc-200';
        case 'Contradiction':
            return 'bg-amber-50 text-amber-700 ring-amber-200';
        case 'Synonym':
            return 'bg-violet-50 text-violet-700 ring-violet-200';
        case 'Duplicate':
            return 'bg-zinc-50 text-zinc-700 ring-zinc-200';
        case 'Overlap':
            return 'bg-zinc-50 text-zinc-700 ring-zinc-200';
        case 'Missing Info':
            return 'bg-amber-50 text-amber-700 ring-amber-200';
        case 'Inconsistency':
            return 'bg-amber-50 text-amber-700 ring-amber-200';
    }
}

// function AvatarPill({ label = 'CG' }: { label?: string }) {
//     return (
//         <div className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-sky-500 p-[2px] shadow-sm">
//             <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-xs font-semibold text-zinc-800">
//                 {label}
//             </div>
//         </div>
//     );
// }

function StatCard({
    title,
    value,
    icon,
    tone = 'zinc',
}: {
    title: string;
    value: number;
    icon: React.ReactNode;
    tone?: 'zinc' | 'blue' | 'emerald' | 'red' | 'violet';
}) {
    const tones: Record<string, string> = {
        zinc: 'bg-white ring-zinc-200',
        blue: 'bg-white ring-blue-200',
        emerald: 'bg-white ring-emerald-200',
        red: 'bg-white ring-red-200',
        violet: 'bg-white ring-violet-200',
    };

    return (
        <div
            className={cx(
                'rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm',
                tones[tone],
            )}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs font-medium text-zinc-500">
                        {title}
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-zinc-900">
                        {value}
                    </div>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200">
                    {icon}
                </div>
            </div>
        </div>
    );
}

function Badge({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <span
            className={cx(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1',
                className,
            )}>
            {children}
        </span>
    );
}

function Icon({
    name,
}: {
    name:
        | 'alert'
        | 'clock'
        | 'check'
        | 'file'
        | 'calendar'
        | 'user'
        | 'chevLeft'
        | 'search';
}) {
    const common = 'h-5 w-5';
    switch (name) {
        case 'alert':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 9v4m0 4h.01M10.29 3.86l-8.4 14.6A2 2 0 0 0 3.62 21h16.76a2 2 0 0 0 1.73-2.54l-8.4-14.6a2 2 0 0 0-3.42 0Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'clock':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 7v5l3 2m7-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'check':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="m5 13 4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'file':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M14 2v6h6"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'calendar':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M8 2v4m8-4v4M3 9h18M5 5h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'user':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 21a8 8 0 1 0-16 0"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                    />
                    <path
                        d="M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'chevLeft':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M15 18 9 12l6-6"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'search':
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="m21 21-4.3-4.3m1.8-5.2a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
    }
}

export default function KnowledgeBaseExceptionsTester() {
    const dispatch = useAppDispatch();
    const { list, listIsLoading, detail, detailIsLoading, error } =
        useAppSelector((s) => s.knowledgeBaseExceptionsSlice);

    const [searchParams] = useSearchParams();
    const exceptionIdFromUrl = searchParams.get('exceptionId');

    const [aiQuota, setAiQuota] = useState<AiQuotaResponse | null>(null);
    const [kbQuota, setKbQuota] = useState<KbQuotaResponse | null>(null);
    const [quotaLoading, setQuotaLoading] = useState(false);
    const [quotaError, setQuotaError] = useState<string | null>(null);

    // ...
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const detailTopRef = React.useRef<HTMLDivElement | null>(null);
    const listTopRef = React.useRef<HTMLDivElement | null>(null);

    async function loadQuotas() {
        setQuotaLoading(true);
        setQuotaError(null);
        try {
            const [aiRes, kbRes] = await Promise.all([
                UsageService.getAiQuota(),
                UsageService.getKbQuota(),
            ]);

            setAiQuota(aiRes.data?.data ?? null);
            setKbQuota(kbRes.data?.data ?? null);
        } catch (e: any) {
            setQuotaError(
                e?.response?.data?.message ||
                    e?.message ||
                    'Failed to load usage quotas',
            );
        } finally {
            setQuotaLoading(false);
        }
    }

    useEffect(() => {
        loadQuotas();
    }, []);

    useEffect(() => {
        if (selectedId) {
            dispatch(tryToFetchKnowledgeBaseExceptionById(selectedId));
        }
    }, [dispatch, selectedId]);

    useEffect(() => {
        if (exceptionIdFromUrl) {
            setSelectedId(exceptionIdFromUrl);
        }
    }, [exceptionIdFromUrl]);

    // Local UI error (e.g. validation), kept separate from API error
    const [localErr, setLocalErr] = useState<string | null>(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState<
        '' | ExceptionStatus | 'all'
    >('all');
    const [categoryFilter, setCategoryFilter] = useState<
        '' | ExceptionCategory | 'all'
    >('all');
    const [reportIdFilter, setReportIdFilter] = useState('');
    const [search, setSearch] = useState('');

    // Detail selection (id from list)

    // Resolve form
    const [explanation, setExplanation] = useState('');
    const [scope, setScope] = useState<KnowledgeScope>('general');
    const [additionalNotes, setAdditionalNotes] = useState('');

    const isDetail = Boolean(selectedId);

    const items: KBException[] = list || [];

    const counts = useMemo(() => {
        const pending = items.filter((x) => x.status === 'pending').length;
        const inProgress = items.filter(
            (x) => x.status === 'in_progress',
        ).length;
        const resolved = items.filter((x) => x.status === 'resolved').length;
        return { pending, inProgress, resolved };
    }, [items]);

    const visibleItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((x) => {
            const hay = [
                x.title,
                x.category,
                x.status,
                x.targetFileName,
                x.document?.name,
                x.context?.text,
                x.context?.snippet,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [items, search]);

    // Load list when filters change
    useEffect(() => {
        dispatch(
            tryToFetchKnowledgeBaseExceptions({
                status: statusFilter,
                category: categoryFilter,
                reportId: reportIdFilter || undefined,
            }),
        );
    }, [dispatch, statusFilter, categoryFilter, reportIdFilter]);

    // Load detail when selection changes
    useEffect(() => {
        if (selectedId) {
            dispatch(tryToFetchKnowledgeBaseExceptionById(selectedId));
        }
    }, [dispatch, selectedId]);

    // Sync resolve form with loaded detail
    useEffect(() => {
        if (detail && selectedId && detail._id === selectedId) {
            setExplanation(detail.resolution?.explanation || '');
            setScope(detail.resolution?.scope || 'general');
            setAdditionalNotes(detail.resolution?.additionalNotes || '');
        } else {
            setExplanation('');
            setScope('general');
            setAdditionalNotes('');
        }
    }, [detail, selectedId]);

    function loadList() {
        dispatch(
            tryToFetchKnowledgeBaseExceptions({
                status: statusFilter,
                category: categoryFilter,
                reportId: reportIdFilter || undefined,
            }),
        );
    }

    async function updateStatus(id: string, status: ExceptionStatus) {
        setLocalErr(null);
        dispatch(clearKnowledgeBaseExceptionsError());
        dispatch(tryToUpdateKnowledgeBaseExceptionStatus({ id, status }));
    }

    async function confirmResolveException(
        id: string,
        confirmationType?: string,
    ) {
        setLocalErr(null);
        dispatch(clearKnowledgeBaseExceptionsError());

        dispatch(
            tryToConfirmResolveKnowledgeBaseException({
                id,
                confirmationType,
            }),
        );
    }

    async function resolveException(id: string) {
        setLocalErr(null);
        dispatch(clearKnowledgeBaseExceptionsError());
        if (!explanation.trim()) {
            setLocalErr('Explanation is required.');
            return;
        }

        dispatch(
            tryToResolveKnowledgeBaseException({
                id,
                explanation: explanation.trim(),
                scope,
                additionalNotes: additionalNotes.trim() || undefined,
            }),
        );
    }

    useEffect(() => {
        if (!selectedId) return;

        // wait a tick so the detail panel is mounted/updated
        const t = window.setTimeout(() => {
            detailTopRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 0);

        return () => window.clearTimeout(t);
    }, [selectedId]);

    // window.setTimeout(() => {
    //     listTopRef.current?.scrollIntoView({
    //         behavior: 'smooth',
    //         block: 'start',
    //     });
    // }, 0);

    useEffect(() => {
        const t = window.setTimeout(() => {
            listTopRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 0);

        return () => window.clearTimeout(t);
    }, []); // only once

    const combinedError = localErr || error;

    return (
        <>
            {/* Usage / Quota cards */}
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 mb-10">
                <UsageQuotaCard
                    title="AI Usage"
                    subtitle="Sofia generated words (rolling 30 days)"
                    used={aiQuota?.wordsUsed ?? 0}
                    limit={aiQuota?.limitWords ?? 150000}
                    percentUsed={aiQuota?.percentUsed ?? 0}
                    unitLabel="words"
                    tone={
                        aiQuota?.percentUsed && aiQuota.percentUsed >= 90
                            ? 'red'
                            : aiQuota?.percentUsed && aiQuota.percentUsed >= 70
                            ? 'amber'
                            : 'blue'
                    }
                    isLoading={quotaLoading && !aiQuota}
                    error={quotaError}
                />

                <UsageQuotaCard
                    title="Knowledge Base"
                    subtitle="Indexed pages (vectors) in Pinecone"
                    used={kbQuota?.usedPages ?? 0}
                    limit={kbQuota?.limitPages ?? 100000}
                    percentUsed={kbQuota?.percentUsed ?? 0}
                    unitLabel="pages"
                    tone={
                        kbQuota?.percentUsed && kbQuota.percentUsed >= 90
                            ? 'red'
                            : kbQuota?.percentUsed && kbQuota.percentUsed >= 70
                            ? 'amber'
                            : 'violet'
                    }
                    isLoading={quotaLoading && !kbQuota}
                    error={quotaError}
                />
            </div>
            <div className="min-h-screen bg-zinc-50">
                <div className="mx-auto max-w-6xl px-6 py-8">
                    {/* Header */}
                    <div className="mb-6 flex items-start justify-between gap-4">
                        <div>
                            <div className="text-sm font-medium text-zinc-500">
                                Knowledge Base
                            </div>
                            <h1 className="mt-1 text-2xl font-semibold text-zinc-900">
                                Knowledge Base Exceptions
                            </h1>
                            <p className="mt-1 text-sm text-zinc-500">
                                Review identified issues, resolve them, and help
                                Sofia learn.
                            </p>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="hidden sm:flex items-center gap-2 rounded-2xl bg-white px-3 py-2 ring-1 ring-zinc-200 shadow-sm">
                                <span className="text-xs font-medium text-zinc-500">
                                    Today
                                </span>
                                <span className="h-4 w-px bg-zinc-200" />
                                <span className="text-xs text-zinc-700">
                                    {new Date().toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            title="Pending"
                            value={counts.pending}
                            icon={<Icon name="alert" />}
                            tone="red"
                        />
                        <StatCard
                            title="In Progress"
                            value={counts.inProgress}
                            icon={<Icon name="clock" />}
                            tone="blue"
                        />
                        <StatCard
                            title="Resolved"
                            value={counts.resolved}
                            icon={<Icon name="check" />}
                            tone="emerald"
                        />
                        <StatCard
                            title="Testing"
                            value={0}
                            icon={<Icon name="file" />}
                            tone="violet"
                        />
                    </div>

                    {/* Optional refresh button */}
                    <div className="mt-3 flex justify-end">
                        <button
                            onClick={loadQuotas}
                            className="rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                            Refresh usage
                        </button>
                    </div>

                    {/* Error */}
                    {combinedError && (
                        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                            {String(combinedError)}
                        </div>
                    )}

                    {/* Layout */}
                    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                        {/* LEFT: list */}
                        <div
                            ref={listTopRef}
                            className={cx(
                                'lg:col-span-1',
                                isDetail && 'hidden lg:block',
                            )}>
                            <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
                                {/* Filters header */}
                                <div className="flex items-center justify-between border-b border-zinc-100 p-4">
                                    <div className="text-sm font-semibold text-zinc-900">
                                        Exceptions
                                    </div>
                                    <button
                                        onClick={loadList}
                                        className="rounded-xl bg-zinc-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-zinc-800">
                                        Refresh
                                    </button>
                                </div>

                                {/* Filters */}
                                <div className="space-y-3 p-4">
                                    <div className="relative">
                                        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                                            <Icon name="search" />
                                        </div>
                                        <input
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            className="w-full rounded-2xl border border-zinc-200 bg-zinc-50 py-2.5 pl-10 pr-3 text-sm text-zinc-900 outline-none ring-0 focus:border-zinc-300"
                                            placeholder="Search exceptions..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-medium text-zinc-600">
                                                Status
                                            </label>
                                            <select
                                                value={statusFilter}
                                                onChange={(e) =>
                                                    setStatusFilter(
                                                        e.target.value as any,
                                                    )
                                                }
                                                className="mt-1 w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none focus:border-zinc-300">
                                                <option value="all">All</option>
                                                <option value="pending">
                                                    Pending
                                                </option>
                                                <option value="in_progress">
                                                    In Progress
                                                </option>
                                                <option value="resolved">
                                                    Resolved
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="text-xs font-medium text-zinc-600">
                                                Category
                                            </label>
                                            <select
                                                value={categoryFilter}
                                                onChange={(e) =>
                                                    setCategoryFilter(
                                                        e.target.value as any,
                                                    )
                                                }
                                                className="mt-1 w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none focus:border-zinc-300">
                                                <option value="all">All</option>
                                                <option value="Hotel Term">
                                                    Hotel Term
                                                </option>
                                                <option value="Contradiction">
                                                    Contradiction
                                                </option>
                                                <option value="Synonym">
                                                    Synonym
                                                </option>
                                                <option value="Duplicate">
                                                    Duplicate
                                                </option>
                                                <option value="Overlap">
                                                    Overlap
                                                </option>
                                                <option value="Missing Info">
                                                    Missing Info
                                                </option>
                                                <option value="Inconsistency">
                                                    Inconsistency
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-medium text-zinc-600">
                                            Report ID (optional)
                                        </label>
                                        <input
                                            value={reportIdFilter}
                                            onChange={(e) =>
                                                setReportIdFilter(
                                                    e.target.value,
                                                )
                                            }
                                            onBlur={loadList}
                                            className="mt-1 w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none focus:border-zinc-300"
                                            placeholder="e.g. 6734f..."
                                        />
                                    </div>
                                </div>

                                {/* List */}
                                <div className="border-t border-zinc-100">
                                    {listIsLoading ? (
                                        <div className="p-4 text-sm text-zinc-500">
                                            Loading…
                                        </div>
                                    ) : visibleItems.length === 0 ? (
                                        <div className="p-4 text-sm text-zinc-500">
                                            No exceptions found.
                                        </div>
                                    ) : (
                                        <ul className="divide-y divide-zinc-100">
                                            {visibleItems.map((x) => (
                                                <li key={x._id}>
                                                    <button
                                                        onClick={() =>
                                                            setSelectedId(x._id)
                                                        }
                                                        className="w-full p-4 text-left hover:bg-zinc-50">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <div className="truncate text-sm font-semibold text-zinc-900">
                                                                        {x.title ||
                                                                            'Untitled'}
                                                                    </div>

                                                                    <Badge
                                                                        className={cx(
                                                                            'ring-1',
                                                                            categoryBadgeClasses(
                                                                                x.category,
                                                                            ),
                                                                        )}>
                                                                        {formatCategory(
                                                                            x.category,
                                                                        )}
                                                                    </Badge>

                                                                    <Badge
                                                                        className={cx(
                                                                            'ring-1',
                                                                            statusBadgeClasses(
                                                                                x.status,
                                                                            ),
                                                                        )}>
                                                                        {formatStatus(
                                                                            x.status,
                                                                        )}
                                                                    </Badge>
                                                                </div>

                                                                <div className="mt-1 text-xs text-zinc-500">
                                                                    {x.document
                                                                        ?.name ||
                                                                        x.targetFileName}
                                                                    {x.createdAt ? (
                                                                        <span className="ml-2">
                                                                            •{' '}
                                                                            {formatWhen(
                                                                                x.createdAt,
                                                                            )}
                                                                        </span>
                                                                    ) : null}
                                                                </div>

                                                                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                                                    <span className="font-semibold">
                                                                        Sofia:
                                                                    </span>{' '}
                                                                    {
                                                                        x.sofiaObservation
                                                                    }
                                                                </div>

                                                                {x.context
                                                                    ?.text ? (
                                                                    <div className="mt-2 text-xs text-zinc-600 line-clamp-2">
                                                                        {
                                                                            x
                                                                                .context
                                                                                .text
                                                                        }
                                                                    </div>
                                                                ) : null}

                                                                <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Icon name="file" />
                                                                        {x
                                                                            .document
                                                                            ?.name ||
                                                                            x.targetFileName}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="shrink-0">
                                                                {/* <AvatarPill label="CG" /> */}
                                                            </div>
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: detail */}
                        <div
                            ref={detailTopRef}
                            className={cx(
                                'lg:col-span-2',
                                !isDetail && 'hidden lg:block',
                            )}>
                            <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
                                {/* Detail header */}
                                <div className="flex flex-col gap-3 border-b border-zinc-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setSelectedId(null)}
                                            className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                                            <Icon name="chevLeft" />
                                            Go Back
                                        </button>

                                        <div className="ml-1 text-sm font-semibold text-zinc-900">
                                            {detail?.exceptionNumber
                                                ? `Exception #${String(
                                                      detail.exceptionNumber,
                                                  ).padStart(4, '0')}`
                                                : 'Exception'}
                                        </div>

                                        {detail?.createdAt ? (
                                            <div className="hidden sm:block text-xs text-zinc-500">
                                                • {formatWhen(detail.createdAt)}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                                        <div className="text-xs text-zinc-500">
                                            {detail?.category
                                                ? formatCategory(
                                                      detail.category,
                                                  )
                                                : ''}
                                        </div>

                                        <select
                                            value={detail?.status || 'pending'}
                                            disabled={
                                                !detail || detailIsLoading
                                            }
                                            onChange={(e) => {
                                                if (!detail) return;
                                                const next = e.target
                                                    .value as ExceptionStatus;
                                                updateStatus(detail._id, next);
                                            }}
                                            className="rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-900 outline-none focus:border-zinc-300">
                                            <option value="pending">
                                                Pending
                                            </option>
                                            <option value="in_progress">
                                                In Progress
                                            </option>
                                            <option value="resolved">
                                                Resolved
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                {/* Detail body */}
                                <div className="p-4">
                                    {!selectedId ? (
                                        <div className="rounded-2xl border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">
                                            Select an exception from the left to
                                            view details.
                                        </div>
                                    ) : detailIsLoading ? (
                                        <div className="text-sm text-zinc-500">
                                            Loading…
                                        </div>
                                    ) : !detail ? (
                                        <div className="text-sm text-zinc-500">
                                            No exception loaded.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                            {/* Left content */}
                                            <div className="lg:col-span-2">
                                                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div
                                                                className="text-xl font-semibold text-zinc-900 break-words whitespace-pre-wrap overflow-wrap-anywhere"
                                                                style={{
                                                                    wordBreak:
                                                                        'break-word',
                                                                }}>
                                                                {detail.title ||
                                                                    'Untitled'}
                                                            </div>

                                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                                <Badge
                                                                    className={cx(
                                                                        'ring-1',
                                                                        categoryBadgeClasses(
                                                                            detail.category,
                                                                        ),
                                                                    )}>
                                                                    {formatCategory(
                                                                        detail.category,
                                                                    )}
                                                                </Badge>
                                                                <Badge
                                                                    className={cx(
                                                                        'ring-1',
                                                                        statusBadgeClasses(
                                                                            detail.status,
                                                                        ),
                                                                    )}>
                                                                    {formatStatus(
                                                                        detail.status,
                                                                    )}
                                                                </Badge>
                                                            </div>
                                                        </div>

                                                        {/* <AvatarPill label="CG" /> */}
                                                    </div>

                                                    <div className="mt-5">
                                                        <div className="text-xs font-semibold text-zinc-700">
                                                            Sofia’s Observation
                                                        </div>
                                                        <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                                            {
                                                                detail.sofiaObservation
                                                            }
                                                        </div>
                                                    </div>

                                                    <div className="mt-5">
                                                        <div className="text-xs font-semibold text-zinc-700">
                                                            Context from
                                                            Document
                                                        </div>
                                                        <div className="mt-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700">
                                                            {detail.context
                                                                ?.text || '—'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Resolve */}
                                                {/* Resolve */}
                                                <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                                                    <div className="text-base font-semibold text-zinc-900">
                                                        Resolve Exception
                                                    </div>

                                                    {/* Quick confirm resolve (no explanation) */}
                                                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                        <button
                                                            disabled={
                                                                detail.status ===
                                                                'resolved'
                                                            }
                                                            onClick={() =>
                                                                confirmResolveException(
                                                                    detail._id,
                                                                    'confirm',
                                                                )
                                                            }
                                                            className={cx(
                                                                'rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm',
                                                                detail.status ===
                                                                    'resolved'
                                                                    ? 'cursor-not-allowed bg-zinc-200 text-zinc-600'
                                                                    : 'bg-emerald-600 text-white hover:bg-emerald-500',
                                                            )}>
                                                            Confirm & Resolve
                                                        </button>

                                                        <button
                                                            disabled={
                                                                detail.status ===
                                                                'resolved'
                                                            }
                                                            onClick={() =>
                                                                confirmResolveException(
                                                                    detail._id,
                                                                    'accept_new',
                                                                )
                                                            }
                                                            className={cx(
                                                                'rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm',
                                                                detail.status ===
                                                                    'resolved'
                                                                    ? 'cursor-not-allowed bg-zinc-200 text-zinc-600'
                                                                    : 'bg-blue-600 text-white hover:bg-blue-500',
                                                            )}>
                                                            Accept New Version
                                                        </button>
                                                    </div>

                                                    {/* Divider */}
                                                    <div className="mt-5 flex items-center gap-3">
                                                        <div className="h-px flex-1 bg-zinc-200" />
                                                        <span className="text-xs text-zinc-500">
                                                            or add explanation
                                                        </span>
                                                        <div className="h-px flex-1 bg-zinc-200" />
                                                    </div>

                                                    {/* Text resolution */}
                                                    <div className="mt-4 grid grid-cols-1 gap-4">
                                                        <div>
                                                            <label className="text-xs font-semibold text-zinc-700">
                                                                Explanation{' '}
                                                                <span className="text-red-500">
                                                                    *
                                                                </span>
                                                            </label>
                                                            <textarea
                                                                value={
                                                                    explanation
                                                                }
                                                                onChange={(e) =>
                                                                    setExplanation(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                rows={4}
                                                                className="mt-2 w-full rounded-2xl border border-zinc-200 px-3 py-3 text-sm"
                                                                placeholder="Provide a clear explanation that Sofia can learn from…"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="text-xs font-semibold text-zinc-700">
                                                                Knowledge Scope{' '}
                                                                <span className="text-red-500">
                                                                    *
                                                                </span>
                                                            </label>
                                                            <select
                                                                value={scope}
                                                                onChange={(e) =>
                                                                    setScope(
                                                                        e.target
                                                                            .value as KnowledgeScope,
                                                                    )
                                                                }
                                                                className="mt-2 w-full rounded-2xl border border-zinc-200 px-3 py-2.5 text-sm">
                                                                <option value="client_specific">
                                                                    Client
                                                                    Specific
                                                                    (this hotel
                                                                    only)
                                                                </option>
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label className="text-xs font-semibold text-zinc-700">
                                                                Additional Notes
                                                                (optional)
                                                            </label>
                                                            <textarea
                                                                value={
                                                                    additionalNotes
                                                                }
                                                                onChange={(e) =>
                                                                    setAdditionalNotes(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                rows={3}
                                                                className="mt-2 w-full rounded-2xl border border-zinc-200 px-3 py-3 text-sm"
                                                            />
                                                        </div>

                                                        <button
                                                            disabled={
                                                                detail.status ===
                                                                'resolved'
                                                            }
                                                            onClick={() =>
                                                                resolveException(
                                                                    detail._id,
                                                                )
                                                            }
                                                            className={cx(
                                                                'w-full rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm',
                                                                detail.status ===
                                                                    'resolved'
                                                                    ? 'cursor-not-allowed bg-zinc-200 text-zinc-600'
                                                                    : 'bg-zinc-900 text-white hover:bg-zinc-800',
                                                            )}>
                                                            {detail.status ===
                                                            'resolved'
                                                                ? 'Resolved'
                                                                : 'Mark as Resolved'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right rail */}
                                            <div className="lg:col-span-1">
                                                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                                                    <div className="text-sm font-semibold text-zinc-900">
                                                        Details
                                                    </div>

                                                    <div className="mt-4 space-y-4 text-sm">
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 text-zinc-400">
                                                                <Icon name="user" />
                                                            </div>
                                                            <div>
                                                                <div className="text-xs font-medium text-zinc-500">
                                                                    Client
                                                                </div>
                                                                <div className="font-medium text-zinc-900">
                                                                    {
                                                                        detail.hotelSub
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 text-zinc-400">
                                                                <Icon name="file" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-medium text-zinc-500">
                                                                    Document
                                                                </div>
                                                                <div className="truncate font-medium text-zinc-900">
                                                                    {detail
                                                                        .document
                                                                        ?.name ||
                                                                        detail.targetFileName}
                                                                </div>
                                                                {detail.document
                                                                    ?.url ? (
                                                                    <a
                                                                        className="mt-1 inline-block text-xs font-medium text-blue-600 hover:underline"
                                                                        href={
                                                                            detail
                                                                                .document
                                                                                .url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noreferrer">
                                                                        Open
                                                                        document
                                                                    </a>
                                                                ) : null}
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 text-zinc-400">
                                                                <Icon name="calendar" />
                                                            </div>
                                                            <div>
                                                                <div className="text-xs font-medium text-zinc-500">
                                                                    Created
                                                                </div>
                                                                <div className="font-medium text-zinc-900">
                                                                    {formatWhen(
                                                                        detail.createdAt,
                                                                    ) || '—'}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs text-blue-800">
                                                            <div className="font-semibold">
                                                                Need Help?
                                                            </div>
                                                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                                                <li>
                                                                    Consult with
                                                                    the client
                                                                    directly
                                                                </li>
                                                                <li>
                                                                    Check
                                                                    similar
                                                                    resolved
                                                                    exceptions
                                                                </li>
                                                                <li>
                                                                    Mark as ‘In
                                                                    Progress’
                                                                    and return
                                                                    later
                                                                </li>
                                                            </ul>
                                                        </div>

                                                        <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-xs text-zinc-600">
                                                            <div className="font-semibold text-zinc-800">
                                                                Evidence
                                                            </div>
                                                            <div className="mt-2 space-y-2">
                                                                <div>
                                                                    <div className="text-[11px] text-zinc-500">
                                                                        New ref
                                                                    </div>
                                                                    <div className="font-medium text-zinc-800">
                                                                        {
                                                                            detail
                                                                                .refs
                                                                                ?.newRef
                                                                                ?.fileName
                                                                        }{' '}
                                                                        / chunk{' '}
                                                                        {
                                                                            detail
                                                                                .refs
                                                                                ?.newRef
                                                                                ?.chunkIndex
                                                                        }
                                                                    </div>
                                                                </div>

                                                                {detail.refs
                                                                    ?.otherRef ? (
                                                                    <div>
                                                                        <div className="text-[11px] text-zinc-500">
                                                                            Other
                                                                            ref
                                                                        </div>
                                                                        <div className="font-medium text-zinc-800">
                                                                            {detail
                                                                                .refs
                                                                                .otherRef
                                                                                .fileName ||
                                                                                '—'}
                                                                        </div>
                                                                        {detail
                                                                            .refs
                                                                            .otherRef
                                                                            .document_url ? (
                                                                            <a
                                                                                href={
                                                                                    detail
                                                                                        .refs
                                                                                        .otherRef
                                                                                        .document_url
                                                                                }
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="mt-1 inline-block text-[11px] font-medium text-blue-600 hover:underline">
                                                                                Open
                                                                                citation
                                                                            </a>
                                                                        ) : null}
                                                                        {typeof detail
                                                                            .refs
                                                                            .otherRef
                                                                            .page_number ===
                                                                        'number' ? (
                                                                            <div className="text-[11px] text-zinc-500">
                                                                                page{' '}
                                                                                {
                                                                                    detail
                                                                                        .refs
                                                                                        .otherRef
                                                                                        .page_number
                                                                                }
                                                                            </div>
                                                                        ) : typeof detail
                                                                              .refs
                                                                              .otherRef
                                                                              .chunkIndex ===
                                                                          'number' ? (
                                                                            <div className="text-[11px] text-zinc-500">
                                                                                chunk{' '}
                                                                                {
                                                                                    detail
                                                                                        .refs
                                                                                        .otherRef
                                                                                        .chunkIndex
                                                                                }
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                ) : null}

                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div className="rounded-xl bg-zinc-50 p-2 ring-1 ring-zinc-200">
                                                                        <div className="text-[11px] text-zinc-500">
                                                                            Confidence
                                                                        </div>
                                                                        <div className="text-sm font-semibold text-zinc-900">
                                                                            {typeof detail
                                                                                .refs
                                                                                ?.confidence ===
                                                                            'number'
                                                                                ? detail.refs.confidence.toFixed(
                                                                                      2,
                                                                                  )
                                                                                : '—'}
                                                                        </div>
                                                                    </div>
                                                                    <div className="rounded-xl bg-zinc-50 p-2 ring-1 ring-zinc-200">
                                                                        <div className="text-[11px] text-zinc-500">
                                                                            Pinecone
                                                                            score
                                                                        </div>
                                                                        <div className="text-sm font-semibold text-zinc-900">
                                                                            {typeof detail
                                                                                .refs
                                                                                ?.pineconeScore ===
                                                                            'number'
                                                                                ? detail.refs.pineconeScore.toFixed(
                                                                                      3,
                                                                                  )
                                                                                : '—'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    if (!detail)
                                                                        return;
                                                                    updateStatus(
                                                                        detail._id,
                                                                        'pending',
                                                                    );
                                                                }}
                                                                className="flex-1 rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                                                                Set Pending
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    if (!detail)
                                                                        return;
                                                                    updateStatus(
                                                                        detail._id,
                                                                        'in_progress',
                                                                    );
                                                                }}
                                                                className="flex-1 rounded-2xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500">
                                                                In Progress
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Mobile hint when no selection */}
                                {!isDetail ? (
                                    <div className="block border-t border-zinc-100 p-4 lg:hidden">
                                        <div className="rounded-2xl border border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-500">
                                            Select an exception to view details.
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
